import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SectionHeader from "@/components/SectionHeader";
import { Reveal } from "@/components/Reveal";
import { useTitle } from "@/hooks/use-title";
import { LEARN_PRODUCTS } from "@/data/content";

export default function Learn() {
  useTitle("Learn — Operating Reality");
  return (
    <main className="pt-16 lg:pt-[72px]" data-testid="learn-page">
      <section className="relative overflow-hidden border-b border-line/50">
        <div className="bg-grid absolute inset-0 opacity-30" />
        <div className="wrap relative py-24 lg:py-32">
          <Reveal>
            <p className="kicker">Learn</p>
            <h1 className="mt-6 max-w-3xl text-balance text-4xl font-semibold leading-[1.08] tracking-tight text-slate-50 sm:text-5xl lg:text-6xl">
              Understand the system before you change it.
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg">
              Diagnostics, guides, toolkits and courses — built on the same method the videos
              and the consulting practice use.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="wrap py-20 lg:py-28">
        <Reveal>
          <div className="grid overflow-hidden border border-glow/30 bg-charcoal/30 lg:grid-cols-12" data-testid="learn-diagnostic-feature">
            <div className="p-8 lg:col-span-8 lg:p-12">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-glow/80">
                Free diagnostic — 15 questions — 3 minutes
              </p>
              <h2 className="mt-5 text-balance text-3xl font-medium leading-[1.15] tracking-tight text-slate-100 lg:text-4xl">
                Operating Reality Diagnostic
              </h2>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-400">
                Fifteen questions to help identify what's really broken before you automate it.
                The result maps your answers across process, people, technology, governance and
                automation — and tells you where to look first. Nothing leaves your browser.
              </p>
              <Link to="/diagnostic" className="btn-primary mt-9" data-testid="learn-diagnostic-cta">
                Take the Diagnostic <ArrowRight size={14} />
              </Link>
            </div>
            <div className="relative hidden border-l border-line/50 lg:col-span-4 lg:block" aria-hidden="true">
              <div className="bg-grid absolute inset-0 opacity-40" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative">
                  <span className="absolute left-1/2 top-1/2 h-24 w-24 -translate-x-1/2 -translate-y-1/2 rounded-full border border-glow/40" />
                  <span className="absolute left-1/2 top-1/2 h-32 w-32 -translate-x-1/2 -translate-y-1/2 rounded-full border border-dashed border-glow/20" />
                  <span className="absolute left-1/2 top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-glow glyph-pulse" />
                </div>
              </div>
            </div>
          </div>
        </Reveal>

        <div className="mt-24">
          <SectionHeader
            kicker="Products"
            title="Built slowly, released when they're ready."
          />
          <div className="mt-12 grid gap-px border border-line/50 bg-line/50 md:grid-cols-2">
            {LEARN_PRODUCTS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.06} className="bg-ink">
                <div className="flex h-full flex-col justify-between gap-10 p-8 lg:p-10" data-testid={`learn-product-card-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <div>
                    <h3 className="text-xl font-medium tracking-tight text-slate-100">{p.title}</h3>
                    <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-400">{p.text}</p>
                  </div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-slate-500">
                    {p.status}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <p className="mt-10 max-w-lg text-sm leading-relaxed text-slate-500">
              Fieldnotes subscribers hear about every release first — including the diagnostic
              guides and the course when it opens.{" "}
              <Link to="/#fieldnotes" className="text-glow underline-offset-4 hover:underline" data-testid="learn-fieldnotes-link">
                Get the Fieldnotes
              </Link>
              .
            </p>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
